import { Howl, Howler } from 'howler';
import { applicationStore } from '../../store/applicationStore';

const SoundType = {
  Music: 'music',
  Win: 'win',
  BetAccepted: 'betAccepted',
};

export class BaseSoundService {
  sounds = {
    [SoundType.Music]: null,
    [SoundType.Win]: null,
    [SoundType.BetAccepted]: null,
  };

  soundSourceIds = {
    [SoundType.Music]: [],
    [SoundType.Win]: [],
    [SoundType.BetAccepted]: [],
  };

  options = [];

  musicOptions = [];

  soundOptions = [];

  constructor({ options }) {
    if (options?.length) {
      this.options = [...options];
    }

    this.options.forEach((option) => {
      if (option.name.includes(SoundType.Music)) {
        this.musicOptions.push(option);
      } else {
        this.soundOptions.push(option);
      }
    });
  }

  async loadResourceVersion2({ language }) {
    const max = this.options.length;
    let count = 1;
    return new Promise((resolve) => {
      this.options.forEach(({ name, getSrc, src, ...option }) => {
        if (typeof getSrc === 'function') {
          this.sounds[name] = new Howl({
            src: getSrc(language),
            ...option,
            onload: () => {
              count += 1;
              if (count === max) {
                resolve();
              }
            },
            onloaderror: () => {
              this.sounds[name] = new Howl({
                src,
                ...option,
                onload: () => {
                  count += 1;
                  if (count === max) {
                    resolve();
                  }
                },
              });
            },
          });
        } else {
          this.sounds[name] = new Howl({
            ...option,
            src,
          });

          this.sounds[name].once('load', () => {
            count += 1;
            if (count === max) {
              resolve();
            }
          });
        }
      });
    });
  }

  async loadResource({ language }) {
    const max = this.options.length;
    return new Promise((resolve, reject) => {
      if (!max) {
        reject();
      }
      this.options.forEach(({ name, getSrc, src, ...option }, index) => {
        if (typeof getSrc === 'function') {
          this.sounds[name] = new Howl({
            src: getSrc(language),
            ...option,
            onload: () => {
              if (index + 1 === max) {
                resolve();
              }
            },
            onloaderror: () => {
              this.sounds[name] = new Howl({
                src,
                ...option,
                onload: () => {
                  if (index + 1 === max) {
                    resolve();
                  }
                },
              });
            },
          });
        } else {
          this.sounds[name] = new Howl({
            ...option,
            src,
            onload: () => {
              this.sounds[name].on('stop', () => {
                this.soundSourceIds[name] = [];
              });
              if (index + 1 === max) {
                resolve();
              }
            },
          });
        }
      });
    });
  }

  async init(initConfig) {
    await this.loadResource(initConfig);
  }

  async playWithoutStop(name, startTime = 0) {
    this.sounds[name].seek(startTime);
    this.soundSourceIds[name] = [...(this.soundSourceIds[name] || []), this.sounds[name].play()];
  }

  async play(name, checkExistence, startTime = 0) {
    if (!checkExistence) {
      await this.stop(name);
    }

    return new Promise((resolve) => {
      if (
        (checkExistence && this.soundSourceIds[name]?.length) ||
        Howler.ctx?.state === 'suspended' ||
        applicationStore.isConnectionLost
      ) {
        // eslint-disable-next-line
        return resolve(false);
      }
      if (this.sounds[name]) {
        this.sounds[name].seek(startTime);
        if (!this.sounds[name].isAddedListener) {
          this.sounds[name].on('end', () => {
            this.soundSourceIds[name] = [];
            resolve(true);
          });
          this.sounds[name].isAddedListener = true;
        }
        // if (!this.soundSourceIds[name]?.[1]) {
        const id = this.sounds[name].play();
        this.soundSourceIds[name] = [...(this.soundSourceIds[name] || []), id];
        // }
        // eslint-disable-next-line
        return resolve(true);
      }
      // eslint-disable-next-line
      return resolve(false);
    });
  }

  async playInDuration(name, checkExistence = false, startTime = 0, duration = null) {
    if (!checkExistence) {
      await this.stop(name); // Stop previous instances if needed
    }

    // eslint-disable-next-line
    return new Promise(async (resolve, reject) => {
      // Handle context suspended state
      if (Howler.ctx?.state === 'suspended') {
        await Howler.ctx.resume();
      }

      // Check if connection is lost
      if (applicationStore.isConnectionLost) {
        // eslint-disable-next-line
        return resolve(false);
      }

      // Check if the sound exists
      if (!this.sounds[name]) {
        // eslint-disable-next-line
        return resolve(false);
      }

      // Ensure the sound is loaded before playing
      if (this.sounds[name].state() !== 'loaded') {
        this.sounds[name].once('load', () => {
          this.playSound(name, resolve, startTime, duration);
        });

        this.sounds[name].once('loaderror', (id, error) => {
          reject(error);
        });
      } else {
        this.playSound(name, resolve, startTime, duration);
      }
    });
  }

  playSound(name, resolve, startTime, duration) {
    // Prevent multiple instances of the same sound from playing unless specified
    if (this.soundSourceIds[name]?.length > 0) {
      return resolve(false); // Sound is already playing
    }

    // Seek to start time if provided
    this.sounds[name].seek(startTime);

    // Play the sound
    const id = this.sounds[name].play();
    this.soundSourceIds[name] = [id]; // Store the sound's play ID

    // Add event listener to clear soundSourceIds when the sound ends
    if (!this.sounds[name].isAddedListener) {
      this.sounds[name].on('end', () => {
        this.soundSourceIds[name] = [];
        resolve(true);
      });
      this.sounds[name].isAddedListener = true;
    }

    // If a duration is specified, stop the sound after the specified number of seconds
    if (duration !== null) {
      setTimeout(() => {
        this.sounds[name].stop(id);
        this.soundSourceIds[name] = [];
        resolve(true);
      }, duration * 1000); // Convert duration to milliseconds
    }

    // If successfully started playing, resolve the promise
    return resolve(true);
  }

  async stop(name) {
    if (this.soundSourceIds[name]?.length) {
      return new Promise((resolve) => {
        this.soundSourceIds[name]?.forEach((id) => {
          this.sounds[name].stop(id);
          resolve(true);
        });
      });
    }
    return new Promise((res) => {
      res();
    });
  }

  mute(name) {
    if (this.sounds[name]) {
      this.sounds[name].mute(true);
    }
  }

  unMute(name) {
    if (this.sounds[name]) {
      this.sounds[name].mute(false);
    }
  }

  async playMusic() {
    await this.play(SoundType.Music);
  }

  async stopMusic() {
    await this.stop(SoundType.Music);
  }

  async playBetAccepted() {
    await this.play(SoundType.BetAccepted);
  }

  async stopBetAccepted() {
    await this.stop(SoundType.BetAccepted);
  }

  async playWin() {
    await this.play(SoundType.Win);
  }

  async stopWin() {
    await this.stop(SoundType.Win);
  }

  async stopAll() {
    await Promise.all(this.options.map(({ name }) => this.stop(name)));
  }

  muteAll() {
    this.options.forEach(({ name }) => this.mute(name));
  }

  unMuteAll() {
    this.options.forEach(({ name }) => this.unMute(name));
  }

  muteMusic(isMute) {
    this.musicOptions.forEach(({ name }) => {
      if (this.sounds[name]) {
        this.sounds[name].mute(isMute);
      }
    });
  }

  muteSounds(isMute) {
    this.soundOptions.forEach(({ name }) => {
      if (this.sounds[name]) {
        this.sounds[name].mute(isMute);
      }
    });
  }

  pauseSounds() {
    this.soundOptions.forEach(({ name }) => {
      if (this.sounds[name] && this.sounds[name].playing()) {
        this.sounds[name].pause();
      }
    });
  }

  pauseMusic() {
    this.musicOptions.forEach(({ name }) => {
      if (this.sounds[name] && this.sounds[name].playing()) {
        this.sounds[name].pause();
      }
    });
  }

  resumeSounds() {
    this.soundOptions.forEach(({ name }) => {
      if (this.sounds[name] && !this.sounds[name].playing()) {
        this.sounds[name].play();
      }
    });
  }

  resumeMusic() {
    this.musicOptions.forEach(({ name }) => {
      if (this.sounds[name] && !this.sounds[name].playing()) {
        this.sounds[name].play();
      }
    });
  }
}
